import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';

const ROLE_CONFIG = {
  admin: {
    label: 'Lead Agent',
    icon: '👑',
    accent: '#d97706',
    accentBg: '#fef3c7',
    items: [
      { icon: '📥', text: 'Gets all team tasks & calendar events' },
      { icon: '📤', text: 'Documents → tasks for entire team' },
      { icon: '👁', text: 'Sees all member progress' },
    ],
  },
  agent: {
    label: 'Agent',
    icon: '🏠',
    accent: '#7c3aed',
    accentBg: '#f5f3ff',
    items: [
      { icon: '📥', text: 'Gets all tasks & calendar events' },
      { icon: '📤', text: 'Documents → tasks for self & assistant' },
      { icon: '👁', text: 'Sees assistant submissions' },
    ],
  },
  member: {
    label: 'Member',
    icon: '👤',
    accent: '#1d4ed8',
    accentBg: '#eff6ff',
    items: [
      { icon: '📥', text: 'Gets own tasks & calendar events only' },
      { icon: '📤', text: 'Documents → tasks for self + Lead Agent' },
    ],
  },
  assistant: {
    label: 'Assistant',
    icon: '🤝',
    accent: '#0e7490',
    accentBg: '#ecfeff',
    items: [
      { icon: '📥', text: 'Gets tasks assigned by Agent' },
      { icon: '📤', text: 'Documents → tasks for self + Agent' },
    ],
  },
  general: {
    label: 'SmartClose Roles',
    icon: '📊',
    accent: '#4f46e5',
    accentBg: '#eef2ff',
    items: [
      { icon: '👑', text: 'Lead Agent: manages all tasks & calendars' },
      { icon: '👤', text: 'Member: receives own tasks only' },
    ],
  },
  saveAction: {
    label: 'Save & Send Invites',
    icon: '✉️',
    accent: '#2563eb',
    accentBg: '#eff6ff',
    items: [
      { icon: '📨', text: 'New or uninvited team members will receive setup invitations.' },
      { icon: '🔒', text: 'Existing members won\'t receive duplicate invites.' },
    ],
  },
  taskReminder: {
    label: 'Due Task Reminder Emails',
    icon: '📧',
    accent: '#2563eb',
    accentBg: '#eff6ff',
    items: [
      { icon: '📅', text: 'Send daily email when this member has tasks due or overdue.' },
    ],
  },
  calendarSync: {
    label: 'Personal Calendar Connection',
    icon: '📆',
    accent: '#2563eb',
    accentBg: '#eff6ff',
    items: [
      { icon: '🔗', text: 'Allow this member to connect their own Google, Outlook, or iCloud calendar.' },
    ],
  },
  deleteTaskLists: {
    label: 'Can Delete Task Lists',
    icon: '🗑️',
    accent: '#ef4444',
    accentBg: '#fee2e2',
    items: [
      { icon: '🗑️', text: 'Allows this person to delete task lists for this connection.' },
    ],
  },
  archiveTaskLists: {
    label: 'Can Archive Task Lists',
    icon: '🗃️',
    accent: '#3b82f6',
    accentBg: '#dbeafe',
    items: [
      { icon: '🗃️', text: 'Allows this Lead Agent to archive task lists for this connection.' },
    ],
  },
  manageTeams: {
    label: 'Team Management',
    icon: '👥',
    accent: '#7c3aed',
    accentBg: '#f5f3ff',
    items: [
      { icon: '👥', text: 'Allows this Lead Agent to view and edit teams they lead.' },
    ],
  },
  viewUnpublished: {
    label: 'View Unshared Changes',
    icon: '⏳',
    accent: '#f97316',
    accentBg: '#fff7ed',
    items: [
      { icon: '⏳', text: 'Allows this Lead Agent to see unshared task changes from team members.' },
    ],
  },
  publish: {
    label: 'Share',
    icon: '📅',
    accent: '#16a34a',
    accentBg: '#dcfce7',
    items: [
      { icon: '📤', text: 'Sends changes to team and calendar' },
    ],
  },
  archive: {
    label: 'Archive',
    icon: '🗃️',
    accent: '#2563eb',
    accentBg: '#dbeafe',
    items: [
      { icon: '🗃️', text: 'Removes this deal from active deals' },
    ],
  },
  delete: {
    label: 'Delete',
    icon: '🗑️',
    accent: '#dc2626',
    accentBg: '#fee2e2',
    items: [
      { icon: '⚠️', text: 'This deal will be deleted' },
    ],
  },
  discard: {
    label: 'Discard',
    icon: '↩️',
    accent: '#ea580c',
    accentBg: '#fff7ed',
    items: [
      { icon: '↩️', text: 'No changes will be saved to this deal' },
    ],
  },
  unarchive: {
    label: 'Unarchive',
    icon: '↩️',
    accent: '#16a34a',
    accentBg: '#dcfce7',
    items: [
      { icon: '↩️', text: 'Returns this deal to active deals.' },
    ],
  },
};

const GAP = 8;
const TOOLTIP_WIDTH = 240;

const TeamNotificationTooltip = ({ children, placement = 'top', role = 'general', wrapperStyle = {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({});
  const [arrowOffset, setArrowOffset] = useState(0);
  const triggerRef = useRef(null);
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.general;

  const handleMouseEnter = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;

    const centerX = rect.left + rect.width / 2;
    let left = centerX - TOOLTIP_WIDTH / 2;
    left = Math.max(8, Math.min(left, vw - TOOLTIP_WIDTH - 8));

    if (placement === 'bottom') {
      setTooltipPos({ top: rect.bottom + GAP, left });
    } else {
      setTooltipPos({ bottom: window.innerHeight - rect.top + GAP, left });
    }
    setArrowOffset(centerX - left);
    setIsVisible(true);
  };

  const isBottom = placement === 'bottom';

  const arrowStyle = {
    position: 'absolute',
    width: 0,
    height: 0,
    left: arrowOffset,
    transform: 'translateX(-50%)',
    ...(isBottom
      ? { bottom: '100%', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: `6px solid ${config.accent}` }
      : { top: '100%',  borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop:  `6px solid ${config.accent}` }
    ),
  };

  const tooltip = isVisible ? (
    <div style={{
      position: 'fixed',
      zIndex: 99999,
      width: TOOLTIP_WIDTH,
      pointerEvents: 'none',
      background: '#fff',
      border: `1.5px solid ${config.accent}`,
      borderRadius: '8px',
      boxShadow: '0 6px 20px rgba(0,0,0,0.14)',
      overflow: 'visible',
      ...tooltipPos,
    }}>
      <div style={arrowStyle} />

      {/* Header */}
      <div style={{
        background: config.accentBg,
        borderBottom: `1px solid ${config.accent}33`,
        borderRadius: '8px 8px 0 0',
        padding: '4px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
      }}>
        <span style={{ fontSize: '12px' }}>{config.icon}</span>
        <span style={{ fontSize: '11px', fontWeight: '700', color: config.accent }}>
          {config.label}
        </span>
      </div>

      {/* Items */}
      <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: '4px', borderRadius: '0 0 8px 8px', background: '#fff' }}>
        {config.items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '11px', color: '#374151', lineHeight: 1.4 }}>
            <span style={{ flexShrink: 0 }}>{item.icon}</span>
            <span>{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsVisible(false)}
        style={{ display: 'inline-block', cursor: 'help', ...wrapperStyle }}
      >
        {children}
      </div>
      {ReactDOM.createPortal(tooltip, document.body)}
    </>
  );
};

export default TeamNotificationTooltip;
